import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";


gsap.registerPlugin(ScrollTrigger);

const processSteps = [
  "Kostenlose Vor-Ort-Beratung und Planung",
  "Installation durch eigene Meister und Monteure",
  "Wartung, E-Check und Notdienst auch nach der Montage",
];

export default function ChargingEasySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    const list = listRef.current;
    const image = imageRef.current;
    const cta = ctaRef.current;
    if (!section || !title || !list || !image || !cta) return;

    const titleLines = title.querySelectorAll("[data-line]");
    const listItems = list.querySelectorAll("[data-item]");
    const ctaLines = cta.querySelectorAll("[data-line]");

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 75%",
        toggleActions: "play none none none",
      },
    });

    tl.fromTo(
      titleLines,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, stagger: 0.08, ease: "power3.out" }
    );

    tl.fromTo(
      listItems,
      { x: 30, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: "power3.out" },
      "-=0.4"
    );

    tl.fromTo(
      ctaLines,
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, stagger: 0.08, ease: "power3.out" },
      "-=0.2"
    );

    const imageTween = gsap.fromTo(
      image,
      { x: -60, opacity: 0, scale: 1.05 },
      {
        x: 0,
        opacity: 1,
        scale: 1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: image,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
      imageTween.scrollTrigger?.kill();
      imageTween.kill();
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-background-100 py-20 md:py-28 lg:py-36 px-6 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 lg:gap-12 items-center">

          {/* Bild links */}
          <div ref={imageRef} className="order-2 md:order-1 md:col-span-5 relative overflow-hidden rounded-br-[40px] flex justify-center max-w-sm mx-auto md:max-w-none md:mx-0 w-full">
            <img
              src="https://www.zweering-elektrotechnik.de/wp-content/uploads/2025/11/ODHUipb5Zn_TAPzcTKR-600x800.webp"
              alt="Zweering Elektrotechnik Mitarbeiter transportiert Kabeltrommel mit Radlader auf Baustelle"
              className="w-full h-72 md:h-[460px] lg:h-[540px] object-cover rounded-bl-[40px]"
            />
          </div>

          {/* Rechts: Headline + zwei Spalten (Text/CTA | Checkliste) */}
          <div className="order-1 md:order-2 md:col-span-7">
            <div ref={titleRef}>
              <h2 className="font-heading font-bold uppercase text-[22px] sm:text-[30px] md:text-[36px] lg:text-[52px] text-foreground-950 tracking-tight leading-[0.95]">
                <span data-line className="block overflow-hidden">
                  <span className="block">SO GEHEN WIR VOR</span>
                </span>
              </h2>
            </div>

            <div className="mt-10 md:mt-16 grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10">
              {/* Intro + CTA */}
              <div>
                <p className="text-foreground-600 text-base leading-relaxed">
                  Von der ersten Beratung bis zum laufenden Service — ein fester Ansprechpartner, ein Team, alles aus einer Hand.
                </p>
                <div ref={ctaRef} className="flex flex-wrap items-center gap-4 mt-8">
                  <span data-line className="block overflow-hidden">
                    <a
                      href="/kontakt"
                      className="inline-flex items-center px-6 py-3.5 rounded-full bg-accent-500 text-white font-body font-medium text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
                    >
                      Beratung vereinbaren
                    </a>
                  </span>
                </div>
              </div>

              {/* Checkliste */}
              <ul ref={listRef} className="space-y-5">
                {processSteps.map((step, index) => (
                  <li key={index} data-item className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center text-white shrink-0 mt-0.5">
                      <i className="ri-check-line w-3 h-3 flex items-center justify-center text-xs" />
                    </span>
                    <span className="text-foreground-700 text-sm leading-relaxed">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}